import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });

  // EmailJS Configuration - Set your EmailJS credentials here
  const EMAILJS_SERVICE_ID = 'service_qa8r1js';
  const EMAILJS_TEMPLATE_ID = 'template_iyurhl6';
  const EMAILJS_PUBLIC_KEY = '1GaTR4wl9qmLbsjyk';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear status message when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if EmailJS is configured
    if (EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID' || 
        EMAILJS_TEMPLATE_ID === 'YOUR_TEMPLATE_ID' || 
        EMAILJS_PUBLIC_KEY === 'YOUR_PUBLIC_KEY') {
      setSubmitStatus({
        type: 'error',
        message: 'EmailJS is not configured. Please set up your EmailJS credentials. See EMAILJS_SETUP.md for instructions.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Initialize EmailJS with public key
      emailjs.init(EMAILJS_PUBLIC_KEY);

      // Prepare template parameters
      // Create a formatted message body containing all contact form details
      const formattedMessageBody = `
Contact Form Submission Details
===============================

Full Name: ${formData.name}
Email Address: ${formData.email}
Organization: ${formData.organization || 'Not provided'}
Phone Number: ${formData.phone || 'Not provided'}
Service Interest: ${formData.service || 'Not specified'}

Message:
--------
${formData.message}

---
This message was sent from the ELIBS SOLUTIONS contact form.
Reply to: ${formData.email}
      `.trim();

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        reply_to: formData.email,
        organization: formData.organization || 'Not provided',
        phone: formData.phone || 'Not provided',
        service: formData.service || 'Not specified',
        message: formData.message,
        message_body: formattedMessageBody
      };

      // Send email
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );

      // Success
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message! We will get back to you soon.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        organization: '',
        phone: '',
        service: '',
        message: ''
      });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again later or contact us directly at directorelibssolutions@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '📧',
      title: 'Email',
      details: 'directorelibssolutions@gmail.com',
      description: 'Send us an email anytime'
    },
    {
      icon: '📞',
      title: 'Phone',
      details: '+94713167976',
      description: 'Contact Director W.R. Sanjeewa'
    },
    {
      icon: '📍',
      title: 'Address',
      details: '242, Gemunu Mawatha, Kotuwegoda, Rajagiriya, Sri Lanka - Postcode: 10107',
      description: 'Visit our head office'
    },
    {
      icon: '🏢',
      title: 'Registration',
      details: 'PV00308441',
      description: 'Company registration number'
    }
  ];

  const services = [
    'Library Automation',
    'Digital Resource Management',
    'Cloud-based Hosting Solutions',
    'Consulting Services',
    'System Integration',
    'Training & Support',
    'Other'
  ];

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Ready to transform your library? Let's discuss your project requirements
          </p>
        </div>
        
        <div className="contact-content">
          <div className="contact-info">
            <h3>Contact Information</h3>
            <p className="contact-description">
              We're here to help you with all your library management needs. 
              Reach out to us through any of the following channels.
            </p>
            
            <div className="contact-methods">
              {contactInfo.map((info, index) => (
                <div key={index} className="contact-method">
                  <div className="method-icon">
                    <span className="icon-emoji">{info.icon}</span>
                  </div>
                  <div className="method-content">
                    <h4 className="method-title">{info.title}</h4>
                    <p className="method-details">{info.details}</p>
                    <p className="method-description">{info.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="company-info">
              <h4>ELIBS SOLUTIONS (PVT) LTD</h4>
              <p><strong>Director:</strong> W.R. Sanjeewa</p>
              <p><strong>Registration No:</strong> PV00308441</p>
              <p>Specialized provider of comprehensive library automation solutions</p>
            </div>
          </div>
          
          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <h3>Send us a Message</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="organization">Organization</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Your organization name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+94 XX XXX XXXX"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="service">Service Interest</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Tell us about your project requirements..."
                ></textarea>
              </div>
              
              {/* Status Messages */}
              {submitStatus.type && (
                <div className={`form-status ${submitStatus.type === 'success' ? 'form-status-success' : 'form-status-error'}`}>
                  {submitStatus.message}
                </div>
              )}
              
              <button 
                type="submit" 
                className="btn btn-primary form-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
